import { Component, ElementRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { GridStack, GridStackOptions } from 'gridstack';
import { DashboardService } from '../services/dashboard.service';
import { catchError, EMPTY, tap } from 'rxjs';
import { NotificationService } from '../services/notification.service';
import { Chart, registerables } from 'chart.js';
import { AuthService } from '../core/auth-service';
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, AfterViewInit {
  constructor(private dashboardService: DashboardService, private notificationService: NotificationService, private authService: AuthService
  ) { }
  @ViewChild('gridContainer', { static: true }) gridContainer!: ElementRef;
  loading: boolean;
  defaultPeriodType: string = 'WTD';
  buyerName: string;
  username: string;
  grid: GridStack;
  resizeObserver: ResizeObserver;
  responsiveLayouts = {
    default: [
      { id: 'grade-match', x: 0, y: 0, w: 1, h: 1 },
      { id: 'cars-flexed-on', x: 1, y: 0, w: 1, h: 1 },
      { id: 'amount-spent', x: 2, y: 0, w: 1, h: 1 },
      { id: 'average-purchase-time', x: 3, y: 0, w: 1, h: 1 },
      { id: 'conversion', x: 0, y: 1, w: 1, h: 2 },
      { id: 'region-rank', x: 1, y: 1, w: 2, h: 2 },
      { id: 'nps', x: 3, y: 2, w: 1, h: 1 },
      { id: 'avg-purchase-price', x: 3, y: 1, w: 1, h: 1},
      { id: 'payments', x: 0, y: 3, w: 1, h: 1 },
    ],
    compact: [
      { id: 'grade-match', x: 0, y: 0, w: 1, h: 1 },
      { id: 'cars-flexed-on', x: 1, y: 0, w: 1, h: 1 },
      { id: 'amount-spent', x: 0, y: 3, w: 1, h: 1 },
      { id: 'average-purchase-time', x: 2, y: 0, w: 1, h: 1 },
      { id: 'conversion', x: 0, y: 1, w: 1, h: 2 },
      { id: 'region-rank', x: 1, y: 1, w: 2, h: 2 },
      { id: 'nps', x: 2, y: 3, w: 1, h: 1 },
      { id: 'avg-purchase-price', x: 1, y: 3, w: 1, h: 1},
      { id: 'payments', x: 0, y: 3, w: 1, h: 1 },
    ]
  }

  async ngOnInit(): Promise<void> {
    Chart.register(...registerables);

    this.loading = true;
    const options: GridStackOptions = {
      disableResize: true,
      disableDrag: true,
      sizeToContent: false,
      cellHeight:265,
      float: true,
      staticGrid: true,
      columnOpts: {
        columnWidth: 310,
        columnMax: 4,
      },
    };
    this.grid = GridStack.init(options, this.gridContainer.nativeElement);
    this.updateGridLayout(this.gridContainer.nativeElement.clientWidth);

    this.getGradeMatchSummary(this.defaultPeriodType, false);
    this.getRegionRankStats(this.defaultPeriodType, 0);
    this.username = this.authService.getUser().username.split('@')[0];
    this.buyerName = this.authService.getUser()?.name;
  }

  ngAfterViewInit(): void{

    document.documentElement.scrollTo(1, 1);

    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        setTimeout(() => {
        this.updateGridLayout(width);
        }, 200);
      }
    })

    this.resizeObserver.observe(this.gridContainer.nativeElement);

    }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setPeriodType(periodType, isPreviousWeek) {
    this.defaultPeriodType = isPreviousWeek ? 'PW' : periodType;
    let periodOffset = 0;
    if(isPreviousWeek) periodOffset = -1;
    this.getGradeMatchSummary(periodType, isPreviousWeek);
    this.getRegionRankStats(periodType, periodOffset);
  }

  getGradeMatchSummary(periodType: string, isPreviousWeek: boolean) {
    return this.dashboardService
      .getGradeMatch$(periodType, isPreviousWeek)
      .pipe(
        tap((result) => {
          this.dashboardService.buyerStatsData.emit(result);
          this.loading = false;
        }),
        catchError((err) => {
          this.notificationService.dangerToast(
            'Unable to view grade match details',
            err
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  getRegionRankStats(periodType: string, periodOffset: number) {
    return this.dashboardService
      .getRegionRank$(periodType, periodOffset)
      .pipe(
        tap((result) => {
          this.dashboardService.regionRankData.emit(result)
          this.loading = false;
        }),
        catchError((err) => {
          this.notificationService.dangerToast(
            'Unable to view grade match details',
            err
          );
          return EMPTY;
        })
      )
      .subscribe();
  }

  updateGridLayout(width: number) {
    if (!this.grid) return;

    const layout = width < 1080 ? this.responsiveLayouts.compact : this.responsiveLayouts.default;

    this.grid.batchUpdate();

    layout.forEach(widget => {
      const el = document.querySelector(`[data-gs-id="${widget.id}"]`) as HTMLElement;
      if(el){
        this.grid.update(el, {
          x: widget.x,
          y: widget.y,
          w: widget.w,
          h: widget.h
        });
      }
    });
  }
}
